import { FC, useRef, useState } from 'react';
import {
  Box,
  Link,
  OnDesktop,
  OnMobile,
  ProfileImage,
  ProfileInitials,
  SimpleLabel,
  Skeleton,
  Text,
} from '@hopskipdrive/react-components';
import { upperFirst } from 'lodash';

import { ApiResponse } from '../../../api/utils/responseTransform';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { useFlags } from '../../../contexts/LDContext';
import { useCategories } from '../../../hooks/useCategories';
import { useGenders } from '../../../hooks/useGenders';
import { formatPhoneParenthesis } from '../../../utils/helpers';
import { RiderServices, SERVICE_ICON_CODES } from '../../features/RiderServices';
import { PausedLabel, ProfilePauseInfo } from '../PauseRider';

import { EditRiderProfile } from './EditRiderProfile';
import { SharedBillingLabel } from './SharedBillingLabel';
import { SharedBillingLabelLegacy } from './SharedBillingLabelLegacy';
import { Age, Container, ContentLabelContainer, RiderContainer, RiderInfo, RiderName } from './styles';

interface RiderProfileProps {
  isFetching?: boolean;
  riderDetails?: RiderDetails;
  saveProfileEdit: (riderFormData) => Promise<ApiResponse<{
    message?: string | undefined;
  }> | null>;
  getHeadingHeight?: (height?: number) => void;
}

export const RiderProfile: FC<RiderProfileProps> = ({
  isFetching,
  riderDetails,
  saveProfileEdit,
  getHeadingHeight,
}) => {
  const riderName = [riderDetails?.firstName, riderDetails?.lastName].filter((name) => name).join(' ');
  const { getLabelByValue } = useCategories();
  const { getLabelByValue: getGenderLabelByValue } = useGenders();
  const [isPictureLoaded, setIsPictureLoaded] = useState<boolean>(false);
  const headingHeight = useRef<HTMLDivElement>(null);

  getHeadingHeight && getHeadingHeight(headingHeight.current?.clientHeight);
  const { enableApprovalFlowForSharedRider, iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;

  const validCodes = SERVICE_ICON_CODES;
  const services = riderDetails?.services?.filter((service) => validCodes.includes(service.code)) || [];

  return (
    <Container>
      {isFetching && !isPictureLoaded && <Skeleton variant="circular" width={96} height={96} />}
      {!isFetching &&
        riderDetails &&
        (riderDetails?.profilePhotoUrl ? (
          <span>
            <ProfileImage
              size={96}
              imgSrc={riderDetails.smallProfilePhotoUrl}
              onLoad={() => setIsPictureLoaded(true)}
            />
          </span>
        ) : (
          <ProfileInitials size={96} fontSize={36} fullName={riderName} onLoad={() => setIsPictureLoaded(true)} />
        ))}
      {!useNewForm && (
        <OnMobile>
          <Can I={actions.edit} a={subjects.rider}>
            <EditRiderProfile
              id={riderDetails?.slug}
              riderDetails={
                riderDetails
                  ? {
                      ...riderDetails,
                      dateOfBirth: new Date(riderDetails.dateOfBirth),
                      gender: getGenderLabelByValue(riderDetails.gender) || '',
                      isAide: !!riderDetails.isAide,
                      photosAttributes: [{ attachment: new File([], '') }],
                      mobilePhoneNumber: riderDetails.mobilePhoneNumber
                        ? riderDetails.mobilePhoneNumber.replace('+1', '')
                        : '',
                    }
                  : undefined
              }
              saveProfileEdit={saveProfileEdit}
            />
          </Can>
        </OnMobile>
      )}
      <RiderContainer>
        <RiderInfo>
          <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
            {isFetching && (
              <Box display="flex" gap="8px">
                <Skeleton variant="text" width={130} height={30} />
                <Skeleton variant="text" width={40} height={30} />
              </Box>
            )}
            {!isFetching && riderDetails ? (
              <RiderName>
                <Text data-testid="rider-name" variant="h1" fontWeight={700} mr={2}>
                  {riderName}
                </Text>
                <Age data-testid="rider-name" fontWeight={300}>{`${riderDetails.age} y/o`}</Age>
              </RiderName>
            ) : null}
            {!isFetching ? <PausedLabel message={riderDetails?.pauseInfo?.message} /> : null}
            {!isFetching && riderDetails?.state === 'deactivated' ? (
              <SimpleLabel label="Deactivated" textColor="white" bgColor="neutral50" />
            ) : null}
            {enableApprovalFlowForSharedRider && !isFetching && riderDetails?.sharedBillingRider ? (
              <SharedBillingLabel sharedRequest={riderDetails.sharedBillingRider} />
            ) : null}
            {!enableApprovalFlowForSharedRider && !isFetching && riderDetails?.sharedBillingRider ? (
              <SharedBillingLabelLegacy
                residenceAccountId={riderDetails.sharedBillingRider.residenceAccountId}
                attendingAccountId={riderDetails.sharedBillingRider.attendingAccountId}
                riderAccountId={riderDetails.accountId}
              />
            ) : null}
            {!isFetching && riderDetails?.isAide ? (
              <SimpleLabel
                label="This rider is an Aide"
                textColor="white"
                bgColor="purple30"
                style={{ display: 'flex' }}
              />
            ) : null}
            <Box marginTop="20px">
              <RiderServices services={services} />
            </Box>
          </Box>
          <Box flexWrap="wrap" columnGap="48px">
            <ContentLabel data-testid="category" label={'Category'}>
              {!isFetching && riderDetails ? (
                getLabelByValue(riderDetails.category)
              ) : (
                <Skeleton variant="text" width={50} />
              )}
            </ContentLabel>
            <ContentLabel label={'ID number'}>
              {!isFetching && riderDetails ? riderDetails.externalId : <Skeleton variant="text" width={50} />}
            </ContentLabel>
            <ContentLabel data-testid="rider-birth-date" label={'Birthday'}>
              {!isFetching && riderDetails ? riderDetails.dateOfBirth : <Skeleton variant="text" width={50} />}
            </ContentLabel>
            <ContentLabel data-testid="rider-gender" label={'Gender'}>
              {!isFetching && riderDetails ? upperFirst(riderDetails.gender) : <Skeleton variant="text" width={50} />}
            </ContentLabel>
            <ContentLabel data-testid="rider-phone-number" label={'Phone'}>
              {!isFetching && riderDetails ? (
                formatPhoneParenthesis(riderDetails.mobilePhoneNumber)
              ) : (
                <Skeleton variant="text" width={50} />
              )}
            </ContentLabel>
            <ContentLabel data-testid="rider-email" label={'Email'}>
              {!isFetching && riderDetails ? (
                riderDetails.email && (
                  <Link data-testid="rider-email" href={`mailto:${riderDetails.email}`} target="_blank">
                    {riderDetails.email}
                  </Link>
                )
              ) : (
                <Skeleton variant="text" width={50} />
              )}
            </ContentLabel>
            <ContentLabel label={'Invoice Group'}>
              {!isFetching && riderDetails ? riderDetails.invoiceGroup?.name : <Skeleton variant="text" width={50} />}
            </ContentLabel>
            {riderDetails?.sharedBillingRider && (
              <ContentLabel data-testid="rider-residence-district" label={'Residence District'}>
                {riderDetails.sharedBillingRider.residenceAccountName}
              </ContentLabel>
            )}
            <ProfilePauseInfo
              pausedAt={riderDetails?.pauseInfo?.pausedAt}
              pausedUntil={riderDetails?.pauseInfo?.pausedUntil}
            />
          </Box>
        </RiderInfo>
      </RiderContainer>
      {!useNewForm && (
        <OnDesktop>
          <Can I={actions.edit} a={subjects.rider}>
            <EditRiderProfile
              id={riderDetails?.slug}
              riderDetails={
                riderDetails
                  ? {
                      ...riderDetails,
                      dateOfBirth: new Date(riderDetails.dateOfBirth),
                      gender: riderDetails.gender.replace('-', '_'),
                      isAide: !!riderDetails.isAide,
                      photosAttributes: [{ attachment: new File([], '') }],
                      mobilePhoneNumber: riderDetails.mobilePhoneNumber
                        ? riderDetails.mobilePhoneNumber.replace('+1', '')
                        : '',
                    }
                  : undefined
              }
              saveProfileEdit={saveProfileEdit}
            />
          </Can>
        </OnDesktop>
      )}
    </Container>
  );
};

const ContentLabel = ({ label, children, ...rest }) => {
  if (!children) {
    return null;
  }

  return (
    <ContentLabelContainer {...rest}>
      <Text variant="h3" textColor="secondaryText">
        {label}
      </Text>
      <Text textColor="secondaryText" fontWeight={300}>
        {children}
      </Text>
    </ContentLabelContainer>
  );
};
